// src/components/PlPanel.tsx (v2.20 — 経営PL タブ、 Codex 再設計後の thin shell)
//
// データ取得 + load/error 表示 + meta header のみ。
// 旧版にあった「月別損益内訳 / 直近12ヶ月累計損益内訳 / 主要指標年計 / 材料費年計 / 最新月+年計カード」
// は Codex 再設計 (2026-05-25) で PlBudgetSection 内の損益トレンド section に統合された。
//
// 経営PL タブの最終構成 (5 セクション、 すべて PlBudgetSection 内):
//   ① サマリー (KPI 7 cards、 当年度累計 / 直近12ヶ月累計 トグル)
//   ② 収益構造と費用率 (旧 100%構成比 + 費用率スプレッドバー 統合)
//   ③ 経常利益 ウォーターフォール (当年度累計のみ)
//   ④ 損益トレンド (月次 / FY累計 / rolling12M モード切替、 指標セレクタ)
//   ⑤ 差異ランキング (当年度累計、 良/悪 TOP 7)

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { PlResponse } from "@shared/api";
import { PlBudgetSection } from "./PlBudgetSection";
import { PlLedgerDrill } from "./PlLedgerDrill";
import { useFilters } from "../store";
import { derivePlSeries } from "../lib/plBasis";
import { useMemo } from "react";
import { plMonthRange, clampPlMonth } from "../lib/plMonthRange";

export function PlPanel({ asOfMonth }: { asOfMonth: string }) {
  const plFormat = useFilters((s) => s.plFormat);
  const plProfit = useFilters((s) => s.plProfit);
  const { data: raw, isLoading, error } = useQuery<PlResponse>({
    queryKey: ["pl"],
    queryFn: api.pl,
    staleTime: 5 * 60_000,
  });
  // [2026-09-11] 経費等 を成分から作り直し、 医業外損益 の行を足す (詳細 = lib/plBasis.ts)。
  //   利益の段階に依らない派生なので fetch も再計算もトグルで走らない。
  const data = useMemo(() => raw ? derivePlSeries(raw) : raw, [raw]);

  if (error) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <div className="font-semibold mb-1">PL 取得エラー</div>
        <pre className="text-xs">{(error as Error).message}</pre>
      </div>
    );
  }
  if (isLoading || !data) {
    return <div className="p-4 text-slate-400 animate-pulse">PL 読み込み中…</div>;
  }
  const incomplete = data.metrics.length === 0
    || data.monthly.length === 0
    || data.rolling12.length === 0
    || !data.dateMax;
  if (incomplete) {
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="font-semibold mb-1">PL データが不完全です</div>
        <ul className="list-disc list-inside text-xs mt-1 space-y-0.5">
          <li>R 側で <code className="bg-amber-100 px-1 rounded">Rscript pl/script/pl_analysis.R</code> を実行</li>
          <li><code className="bg-amber-100 px-1 rounded">Rscript script/50_dashboard_build/build_dashboard_db.R</code> を実行</li>
          <li>dashboard server を再起動</li>
        </ul>
      </div>
    );
  }
  // 対象月は PL の実績月の範囲へ寄せて渡す (サイドバーの対象月セレクタと同じ規則・lib/plMonthRange.ts)
  const plAsOf = clampPlMonth(asOfMonth, plMonthRange(data, plFormat));
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">💰 経営PL — 損益分析 (狭山地区: 埼玉石心会病院)</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            単位: 千円 (グラフ 百万円) / 表示形式 <b>{plFormat}</b> / 利益の段階 <b>{plProfit}</b>
          </p>
        </div>
        <span className="perf-pill">SQL {data.sqlMs} ms</span>
      </div>
      <PlBudgetSection data={data} asOfMonth={plAsOf} format={plFormat} profit={plProfit} />
      {/* 2026-09-26: 元帳 (仕訳) まで深掘り。 差異ランキングの科目名クリックでもここへ来る */}
      <PlLedgerDrill asOfMonth={plAsOf} />
    </div>
  );
}
